<template>
  <div id="store-prices-table">
    <b-table
      class="sticky-table-header is-contained"
      :data="products"
      narrowed
      hoverable
      scrollable
      :paginated="true"
      :per-page="50"
      pagination-position="both"
      pagination-order="is-centered"
      aria-next-label="Próxima página"
      aria-previous-label="Página anterior"
      aria-page-label="Página"
      aria-current-label="Página atual"
      :debounce-search="500"
      detailed
      detail-key="b"
      :show-detail-icon="true"
      custom-detail-row
      @details-open="openDetails"
      @details-close="closeDetails"
    >
      <b-table-column
        field="b"
        :label="$t('products.code')"
        sortable
        searchable
        v-slot="props"
      >
        <span class="adjust-top">{{ props.row.b }}</span>
      </b-table-column>

      <b-table-column
        field="n"
        :label="$t('products.name')"
        sortable
        searchable
        v-slot="props"
      >
        <span class="adjust-top">{{ props.row.n }}</span>
      </b-table-column>

      <b-table-column
        field="lc"
        :label="$t('products.cost')"
        sortable
        v-slot="props"
        v-if="permissions.seeProductCostTableColumn"
      >
        <div class="cost-input-wrapper">
          <money
            v-model="props.row.lc"
            v-bind="money"
            class="input is-expanded"
            :class="{ 'is-cost-saving': isSavingCost(props.row.b) }"
            @blur.native="updateCost(props.row)"
            @focus.native="registerOriginalCost(props.row.lc)"
            style="width: 100px"
            :disabled="
              !permissions.addUpdatePurchase || isSavingCost(props.row.b)
            "
          ></money>

          <span v-if="isSavingCost(props.row.b)" class="cost-saving-overlay">
            <b-icon
              icon="sync-alt"
              custom-class="fa-spin"
              size="is-small"
              :style="{ color: $primaryColor }"
            ></b-icon>
          </span>
        </div>
      </b-table-column>

      <b-table-column
        field="p"
        :label="$t('products.price')"
        sortable
        v-slot="props"
      >
        <div class="price-cell">
          <money
            v-model="props.row.p"
            v-bind="money"
            :key="`master-${props.row.b}-${props.row.revertToken}`"
            :ref="`master-${props.row.b}`"
            class="input is-expanded"
            :class="{ 'is-changed': hasPendingChange(props.row) }"
            @blur.native="commitMasterPrice(props.row)"
            @focus.native="registerOriginalMasterPrice(props.row)"
            style="width: 100px"
            :disabled="!permissions.addProductToCatalogUpdatePrice"
          ></money>

          <b-tooltip
            v-if="!props.row.uniform || isDetailOpen(props.row.b)"
            :label="
              props.row.uniform
                ? $t('storePrices.priceHint')
                : $t('storePrices.differentPricesHint')
            "
            position="is-top"
            multilined
          >
            <b-icon
              icon="info-circle"
              type="is-info"
              size="is-small"
              class="store-prices-info"
            ></b-icon>
          </b-tooltip>
        </div>
      </b-table-column>

      <b-table-column
        field="m"
        :label="$t('products.margin')"
        sortable
        v-slot="props"
        v-if="permissions.seeProductMarginTableColumn"
      >
        <span class="adjust-top">{{ marginLabel(props.row) }}</span>
      </b-table-column>

      <template slot="detail" slot-scope="props">
        <tr
          v-for="store in props.row.storePrices"
          :key="`${props.row.b}-${store.storeId}`"
          class="store-price-row"
        >
          <td></td>

          <td colspan="2" class="store-price-name">
            {{ store.storeName }}
          </td>

          <td v-if="permissions.seeProductCostTableColumn"></td>

          <td>
            <money
              v-model="store.price"
              v-bind="money"
              :key="`${store.storeId}-${store.revertToken}`"
              class="input is-expanded"
              :class="{ 'is-changed': isStorePriceChanged(store) }"
              @blur.native="commitStorePrice(props.row, store)"
              @focus.native="registerOriginalStorePrice(store)"
              style="width: 100px"
              :disabled="!permissions.addProductToCatalogUpdatePrice"
            ></money>
          </td>

          <td v-if="permissions.seeProductMarginTableColumn">
            {{ formatMargin(props.row.lc, store.price) }}
          </td>
        </tr>
      </template>

      <template slot="empty"
        ><table-empty-template
      /></template>
    </b-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Money } from "v-money";
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";
import tableEmptyTemplate from "../common/TableEmptyTemplate";

export default {
  props: ["products", "savingCostBarcode"],

  mixins: [common, mask],

  components: { Money, tableEmptyTemplate },

  data() {
    return {
      money: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2
      },
      initialPrice: 0,
      initialCost: 0,
      openedBarcodes: []
    };
  },

  computed: {
    ...mapGetters(["permissions", "payments"])
  },

  methods: {
    isDetailOpen(barcode) {
      return this.openedBarcodes.includes(barcode);
    },

    openDetails(row) {
      if (!this.isDetailOpen(row.b)) this.openedBarcodes.push(row.b);
    },

    closeDetails(row) {
      const index = this.openedBarcodes.indexOf(row.b);
      if (index > -1) this.openedBarcodes.splice(index, 1);
    },

    registerOriginalMasterPrice(row) {
      this.initialPrice = row.p;
    },

    // Aplica o preço do campo principal a todas as lojas do produto. Sair do
    // campo sem informar um preço apenas desiste da edição.
    commitMasterPrice(row) {
      if (!(row.p > 0) || row.p === this.initialPrice) {
        if (!(row.p > 0)) this.revertPrice(row);
        this.refreshRow(row);
        return;
      }

      row.storePrices.forEach(store => {
        if (store.price === row.p) return;

        store.price = row.p;
        this.emitStorePrice(row, store);
      });

      this.refreshRow(row);
    },

    registerOriginalStorePrice(store) {
      this.initialPrice = store.price;
    },

    commitStorePrice(row, store) {
      if (store.price === this.initialPrice) return;

      if (store.price <= 0) {
        // Esta tela altera preço; retirar o item do catálogo continua sendo
        // feito na tela de produtos da loja, com a confirmação de sempre.
        store.price = this.initialPrice;
        this.revertPrice(store);
        this.alertError({ message: "INVALID_PRICE" });
        return;
      }

      this.emitStorePrice(row, store);
      this.refreshRow(row);
    },

    // O campo de dinheiro mantém o próprio valor formatado e ignora um valor
    // devolvido pelo código, então continuaria exibindo o preço recusado.
    // Trocar a chave o remonta já com o valor correto.
    revertPrice(target) {
      target.revertToken += 1;
    },

    emitStorePrice(row, store) {
      this.$emit("update-store-price", {
        barcode: row.b,
        storeId: store.storeId,
        p: this.formatMoneyToCents(store.price),
        isSavedValue: !this.isStorePriceChanged(store)
      });
    },

    // Preço, margem e uniformidade são campos da linha para que a tabela
    // consiga ordenar por eles, então precisam ser refeitos a cada alteração.
    refreshRow(row) {
      const prices = row.storePrices.map(store => store.price);
      const lowest = Math.min(...prices);
      const highest = Math.max(...prices);

      row.uniform = lowest === highest;
      // Com preços diferentes não há valor único a exibir. O campo parte do
      // maior preço praticado, e o tooltip diz que é isso que está ali.
      row.p = highest;
      row.m = row.uniform && row.lc > 0 ? (lowest - row.lc) / lowest : null;
    },

    isStorePriceChanged(store) {
      return store.price !== store.savedPrice;
    },

    hasPendingChange(row) {
      return row.storePrices.some(store => this.isStorePriceChanged(store));
    },

    marginLabel(row) {
      return row.m === null || isNaN(row.m)
        ? "-"
        : `${(row.m * 100).toFixed(1)} %`;
    },

    formatMargin(cost, price) {
      if (!(price > 0) || !(cost > 0)) return "-";
      return `${(((price - cost) / price) * 100).toFixed(1)} %`;
    },

    registerOriginalCost(cost) {
      this.initialCost = cost || 0;
    },

    updateCost(row) {
      const currentCost = row.lc || 0;
      if (currentCost === this.initialCost) return;

      this.$emit("update-cost", {
        barcode: row.b,
        name: row.n,
        lc: this.formatMoneyToCents(currentCost)
      });

      this.refreshRow(row);
    },

    isSavingCost(barcode) {
      return this.savingCostBarcode === barcode;
    }
  },

  mounted() {
    this.money.prefix = `${this.payments.currencySymbol} `;
  }
};
</script>

<!-- A paginação nasce dentro da tabela do Buefy, que a encosta à direita.
     As outras telas da aplicação usam a paginação centralizada. -->
<style>
#store-prices-table .level .level-right {
  justify-content: center;
  width: 100%;
}
</style>

<style scoped>
.adjust-top {
  position: relative;
  top: 8px;
}

.price-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.store-prices-info {
  cursor: help;
}

.cost-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.cost-input-wrapper .input.is-cost-saving {
  padding-right: 2rem;
}

.cost-saving-overlay {
  position: absolute;
  top: 50%;
  right: 0.65rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  pointer-events: none;
}

.store-price-row td {
  background-color: hsl(0, 0%, 98%);
  vertical-align: middle;
}

.store-price-name {
  padding-left: 1.5rem !important;
  font-size: 0.9rem;
  color: hsl(0, 0%, 29%);
}

.store-price-row .input.is-changed {
  border-color: hsl(48, 100%, 46%);
}
</style>
